import {
  ApprovedUserResponse,
  ChangeDownPaymentArgs,
  CheckPurchaseAmountResponse,
  CompleteEstimateArguments,
  CustomerVerificationLinkResponse,
  CreatePrequalApplicationResponse,
  DocumentPage,
  BackendService,
  IApplyCharge,
  Init,
  LoanApplicationRequest,
  LoginArguments,
  LoginResponse,
  Noaa5Document,
  NoaaDocument,
  NotificationDocument,
  OnRefreshFailure,
  SendOTPArguments,
  UpdatePaymentMethod,
  ValidateDebitCard,
  ValidateUserResponse,
  WithWorkflowState,
} from './models';
import Customer from '@sunbit-dev/common/models/ICustomer';
import OnlineApplicationMerchant from '@sunbit-dev/common/models/IOnlineApplicationMerchant';
import { Offer } from '@sunbit-dev/purchase-common/src/models/Offer';
import { NetworkService } from '@sunbit-dev/purchase-common/src/services/networkService/NetworkService';
import { getCustomerForLogs } from '@sunbit-dev/purchase-common/src/helpers/logs';
import { NetworkServiceError } from '@sunbit-dev/purchase-common/src/services/networkService/models';
import { PurchaseInfo, FinalPageInfo } from '~/models/Purchase';
import { WorkflowState } from '~/models/WorkflowState';
import { EventsNames } from '~/enums/Events';
import { NotificationType } from '~/enums/NotificationType';
import { Status } from '@sunbit-dev/purchase-common/src/enums/guided-app/Status';
import { loanApplicationNetworkService } from '~/services/LoanApplicationNetworkService/index';
import { logsService } from '~/services/LogsService';
import deviceId from '@sunbit-dev/common/services/deviceId';

export class LoanApplicationNetworkService extends NetworkService {
  private shortToken: string;

  private baseUrls: Record<BackendService, string>;

  private isRefreshAccessTokenActive: boolean = false;

  private refreshingTokenPromise: Promise<void | LoginResponse> = Promise.resolve();

  private tokenExpiration: number;

  private onAuthenticationRequired: () => void;

  private onWorkflowState?: (workflowState: WorkflowState) => void;

  constructor({ onAuthenticationRequired, onWorkflowState, baseUrls, ...props }: Init) {
    super({ addNetworkErrorLogs: logsService.addNetworkError, ...props });

    this.baseUrls = baseUrls;
    this.onAuthenticationRequired = onAuthenticationRequired;
    this.onWorkflowState = onWorkflowState;
  }

  public async request<T>(args: LoanApplicationRequest): Promise<T> {
    // If the access token is under a refresh wait till its refreshed.
    // Or if there is no network connection wait till its back online.
    try {
      await this.refreshingTokenPromise;
      await this.networkStatusPending.promise;
    } catch (error) {
      // this log already monitored in refresh token promise
      console.info(error);
    }

    const response = await super.request<T>({ ...args, baseUrl: this.baseUrls[args.service] });

    const workflowState = (response as { _workflowState?: WorkflowState } | null | undefined)?._workflowState;
    if (workflowState) {
      this.onWorkflowState?.(workflowState);
    }

    return response;
  }

  private waitForAccessTokenToBeValid = async () => {
    new Promise<void>((r) => {
      setInterval(() => {
        if (this.tokenExpiration > new Date().getTime()) r();
      }, 300);
    });
  };

  setShortToken = ({ shortToken }) => {
    this.shortToken = shortToken;
    if (shortToken) {
      this.addHeader('efilogid', shortToken);
    } else {
      this.removeHeader('efilogid');
    }
  };

  getShortToken = () => {
    return this.shortToken;
  };

  clearShortToken = () => {
    this.shortToken = '';
    this.removeHeader('efilogid');
  };

  setTokenExpiration = ({ tokenExpiration }: { tokenExpiration: number }) => {
    this.tokenExpiration = tokenExpiration;
  };

  getTokenExpiration = () => {
    return this.tokenExpiration;
  };

  public disableAccessTokenRefresh = () => {
    this.isRefreshAccessTokenActive = false;
  };

  public revokeAccess = () => {
    loanApplicationNetworkService.disableAccessTokenRefresh();
    loanApplicationNetworkService.removeToken();
    loanApplicationNetworkService.clearShortToken();
  };

  public login = async ({
    verificationCode,
    pinCodeMethod,
    eventName,
    onRefreshFailure,
  }: LoginArguments): Promise<LoginResponse> => {
    const response = await this.request<LoginResponse>({
      method: 'post',
      url: `/api/v2/loan-applications/${this.shortToken}/loginOTP`,
      service: BackendService.Purchase,
      data: {
        notificationMethod: pinCodeMethod || null,
        verificationCode: verificationCode || null,
      },
      withCredentials: true, // store the refresh-token cookie returned by this endpoint
      trackingInfo: {
        eventName,
      },
    });

    if (response.token) this.setToken(response.token);
    if (response.expiresIn) {
      this.setTokenExpiration({ tokenExpiration: response.expiresIn });
      this.setAccessTokenTimer({ onRefreshFailure });
    }

    return response;
  };

  public sendPinCode = async ({ pinCodeMethod, phoneNumber, eventName }: SendOTPArguments): Promise<void> => {
    await this.request({
      method: 'post',
      url: `/api/v2/loan-applications/${this.shortToken}/sendOTP`,
      service: BackendService.Purchase,
      data: {
        notificationMethod: pinCodeMethod,
        phoneNumber,
      },
      trackingInfo: {
        eventName,
      },
    });
  };

  public setAccessTokenTimer = async ({ onRefreshFailure }: { onRefreshFailure: OnRefreshFailure }) => {
    this.isRefreshAccessTokenActive = true;

    while (this.isRefreshAccessTokenActive) {
      // Waiting for the expiration time to arrive - Minus two minutes
      await new Promise((resolve) => setTimeout(resolve, this.tokenExpiration - new Date().getTime() + -2 * 60 * 1000));

      if (!this.isRefreshAccessTokenActive) break;

      try {
        this.removeToken();

        // Keep refreshing token
        this.refreshingTokenPromise = this.refreshCredentials({ timeout: this.tokenExpiration });

        const response = (await this.refreshingTokenPromise) as LoginResponse;

        if (response.token) {
          this.setToken(response.token);
          this.setTokenExpiration({ tokenExpiration: response.expiresIn });
        }
      } catch (error) {
        // Check the type of error
        if ((error as NetworkServiceError).code === 'RefreshTokenAuthenticationFailed') {
          this.onAuthenticationRequired();
        } else {
          await onRefreshFailure({ error: error as NetworkServiceError });
        }

        // Stop refreshing on failure
        this.disableAccessTokenRefresh();
      }
    }
  };

  public refreshCredentials = async ({ timeout }: { timeout: number }) => {
    const response = await this.request<LoginResponse>({
      method: 'post',
      url: `/api/v2/loan-applications/${this.shortToken}/token/refresh`,
      service: BackendService.Purchase,
      withCredentials: true, // send the refresh-token cookie to mint a new access token
      trackingInfo: {
        eventName: EventsNames.refreshToken,
        props: {
          timeout,
        },
      },
    });

    return response;
  };

  public getPurchaseInitialInfo = async (): Promise<Partial<PurchaseInfo>> => {
    const response = await this.request<Partial<PurchaseInfo>>({
      url: `/api/v2/loan-applications/${this.shortToken}/initial-info`,
      method: 'get',
      service: BackendService.LoanApplication,
      trackingInfo: { eventName: EventsNames.getPurchaseInitialInfo },
    });

    return response;
  };

  public getPurchaseInfo = async (): Promise<PurchaseInfo> => {
    const response = await this.request<PurchaseInfo>({
      url: '/api/v2/loan-applications/info',
      method: 'get',
      service: BackendService.LoanApplication,
      trackingInfo: { eventName: EventsNames.getPurchaseInfo },
    });

    return response;
  };

  public getFinalPageInfo = async (): Promise<FinalPageInfo> => {
    return this.request<FinalPageInfo>({
      url: '/api/v2/loan-applications/post-loan-info',
      method: 'get',
      service: BackendService.Purchase,
      trackingInfo: { eventName: EventsNames.getFinalPageInfo },
    });
  };

  public updateCustomer = async ({
    customer,
    customerVerificationId,
  }: {
    customer: Partial<Customer>;
    customerVerificationId: string;
  }): Promise<Customer & WithWorkflowState> => {
    return this.request<Customer & WithWorkflowState>({
      url: '/api/v2/loan-applications/customer',
      service: BackendService.Purchase,
      params: { cvid: customerVerificationId },
      method: 'post',
      data: customer,
      trackingInfo: {
        eventName: EventsNames.updateCustomer,
        props: {
          customer: getCustomerForLogs(customer),
        },
      },
    });
  };

  public updateApplicationStatus = async ({
    status,
    loanApplicationId,
    offerId,
  }: {
    status: Status;
    loanApplicationId: number;
    offerId?: number;
  }): Promise<void> => {
    return await this.request({
      url: `/api/v2/loan-applications/${loanApplicationId}/status`,
      service: BackendService.Purchase,
      method: 'put',
      data: { status, offerId },
      trackingInfo: {
        eventName: EventsNames.updateApplicationStatus,
        props: {
          status,
        },
      },
    });
  };

  public getApplicationStatus = async ({
    loanApplicationId,
  }: {
    loanApplicationId: number;
  }): Promise<{ status: Status } & WithWorkflowState> => {
    const response = await this.request<{ status: Status } & WithWorkflowState>({
      url: `/api/v2/loan-applications/${loanApplicationId}/status`,
      service: BackendService.Purchase,
      method: 'get',
      trackingInfo: { eventName: EventsNames.getApplicationStatus },
    });
    return response;
  };

  public fetchOffer = async ({ ssn, isMock }: { ssn?: string; isMock?: boolean }): Promise<Offer> => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const customHeaders = isMock ? { 'Is-Mock': 'true' } : {};

    return this.request<Offer>({
      method: 'post',
      url: 'api/v2/loan-applications/purchase',
      service: BackendService.Purchase,
      data: { ssn, timeZone },
      headers: { ...customHeaders },
      trackingInfo: {
        eventName: EventsNames.fetchOffer,
        props: { timeZone },
      },
    });
  };

  public getActiveOffer = async (): Promise<Offer> => {
    return this.request<Offer>({
      method: 'get',
      url: 'api/v2/loan-applications/purchase/active-offer',
      service: BackendService.Purchase,
      trackingInfo: {
        eventName: EventsNames.getActiveOffer,
      },
    });
  };

  public cancelPurchase = async ({ offerId }: { offerId: string }): Promise<void> => {
    await this.request({
      url: `api/v2/loan-applications/purchase/cancel/${offerId}`,
      service: BackendService.Purchase,
      method: 'post',
    });
  };

  public updateCustomerWaitingForPurchaseAmount = async ({ eventName }): Promise<void> => {
    await this.request<CheckPurchaseAmountResponse>({
      method: 'post',
      url: 'api/v2/loan-applications/request-to-checkout',
      service: BackendService.Purchase,
      trackingInfo: {
        eventName,
      },
    });
  };

  public checkPurchaseAmount = async ({ eventName }): Promise<CheckPurchaseAmountResponse> => {
    return this.request<CheckPurchaseAmountResponse>({
      url: 'api/v2/loan-applications/check-amount',
      service: BackendService.Purchase,
      trackingInfo: {
        eventName,
      },
    });
  };

  public fetchPreQualificationOffer = async ({
    ssn,
    offerId,
    amount,
  }: {
    ssn?: string;
    offerId: number;
    amount: number;
  }): Promise<Offer> => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return this.request<Offer>({
      method: 'put',
      url: `api/v2/loan-applications/purchase/${offerId}/amount`,
      service: BackendService.Purchase,
      data: { ssn, timeZone, purchaseAmount: amount },
      trackingInfo: {
        eventName: EventsNames.fetchPreQualificationOffer,
        props: { timeZone, purchaseAmount: amount },
      },
    });
  };

  public completeEstimate = async ({ purchaseId, eventName }: CompleteEstimateArguments): Promise<void> => {
    await this.request({
      method: 'post',
      url: `/api/v2/loan-applications/purchase/${purchaseId}/estimate-completed`,
      service: BackendService.Purchase,
      trackingInfo: {
        eventName: eventName,
      },
    });
  };

  public resendEmail = async (): Promise<Customer> => {
    return this.request({
      url: 'api/v2/loan-applications/resend-verification-email',
      service: BackendService.Purchase,
      method: 'post',
      trackingInfo: {
        eventName: EventsNames.resendVerificationEmail,
        props: {},
      },
    });
  };

  public selectPlan = async ({ offerId, paymentPlanId }: { offerId: number; paymentPlanId: number }): Promise<void> => {
    return this.request({
      method: 'put',
      url: `/api/v2/loan-applications/${offerId}/payment-plan/${paymentPlanId}`,
      service: BackendService.Purchase,
      trackingInfo: {
        eventName: EventsNames.selectPlan,
        props: {
          offerId,
          paymentPlanId,
        },
      },
    });
  };

  public verifyEmail = async ({ cvid }: { cvid: string }): Promise<{ customer: Customer }> => {
    return this.request({
      method: 'put',
      url: '/api/v2/loan-applications/email-verification',
      service: BackendService.Purchase,
      data: { customerVerificationID: cvid },
      trackingInfo: {
        eventName: EventsNames.emailVerify,
        props: { cvid },
      },
    });
  };

  public checkUserMlaAllowance = async ({ offerId }: { offerId: number }): Promise<boolean> => {
    return this.request({
      method: 'get',
      url: `api/v2/loan-applications/${offerId}/mla`,
      service: BackendService.Purchase,
      trackingInfo: {
        eventName: EventsNames.checkUserMla,
        props: { offerId },
      },
    });
  };

  public updatePaymentMethod = async ({
    offerId,
    creditCard,
    isRccPurchase,
    isMailPaymentMethod,
    paymentMethodType,
    accountNumber,
    routingNumber,
  }: UpdatePaymentMethod): Promise<WithWorkflowState> => {
    const payload = {
      accountNumber,
      routingNumber,
      isRccPurchase,
      isMailPaymentMethod,
      isCardWasSwiped: false,
      paymentMethodType,
      ...creditCard,
    };

    return this.request<WithWorkflowState>({
      method: 'put',
      url: `api/v2/loan-applications/${offerId}/offers/debitCard`,
      service: BackendService.Purchase,
      data: payload,
      trackingInfo: {
        eventName: EventsNames.updatePaymentMethod,
        props: { offerId, isRccPurchase, isMailPaymentMethod, paymentMethodType },
      },
    });
  };

  public validateDebitCard = async ({ offerId, creditCard }: ValidateDebitCard) => {
    const payload = { ...creditCard };

    return this.request({
      method: 'put',
      url: `api/v2/loan-applications/${offerId}/offers/debitCard`,
      service: BackendService.Purchase,
      data: payload,
      trackingInfo: {
        eventName: EventsNames.validateDebitCard,
        props: { offerId },
      },
    });
  };

  public getDocumentPage = async <T>(documentType: T): Promise<DocumentPage> => {
    return this.request({
      method: 'get',
      url: `api/v1/documents/generate/${documentType}`,
      service: BackendService.Purchase,
      trackingInfo: {
        eventName: EventsNames.getDocument,
        props: { documentType },
      },
    });
  };

  public sendEmailAgreementDraft = async (offerId: number) => {
    return this.request({
      method: 'post',
      url: `api/v2/loan-applications/${offerId}/agreement-draft`,
      service: BackendService.Purchase,
      trackingInfo: {
        eventName: EventsNames.sendAgreement,
        props: { offerId },
      },
    });
  };

  public fetchUserLoanAgreement = async ({ offerId }: { offerId: number }): Promise<DocumentPage> => {
    return this.request({
      method: 'get',
      url: `api/v2/loan-applications/${offerId}/generate/agreement-draft`,
      service: BackendService.Purchase,
      trackingInfo: {
        eventName: EventsNames.getUserLoadAgreement,
        props: { offerId },
      },
    });
  };

  public fetchRiskAgreement = async ({ offerId }: { offerId: number }): Promise<any> => {
    return this.request({
      method: 'get',
      url: `api/v2/loan-applications/${offerId}/risk-based-pricing-agreement`,
      service: BackendService.Purchase,
      trackingInfo: {
        eventName: EventsNames.getUserRiskAgreement,
        props: { offerId },
      },
    });
  };

  public applyCharge = async (offerId: number, applyCharge: IApplyCharge) => {
    return this.request({
      method: 'post',
      url: `api/v2/loan-applications/${offerId}/charges`,
      service: BackendService.Purchase,
      data: applyCharge,
      trackingInfo: {
        eventName: EventsNames.applyCharge,
        props: { offerId },
      },
    });
  };

  public updateUserSsn = async ({ offerId, ssn }: { offerId: number; ssn: string }): Promise<boolean> => {
    return this.request({
      method: 'post',
      url: `api/v2/loan-applications/${offerId}/ssn`,
      service: BackendService.Purchase,
      data: {
        ssn,
      },
      trackingInfo: {
        eventName: EventsNames.updateUserSsnWithLoanAgreement,
        props: { offerId },
      },
    });
  };

  public changeDownPayment = ({ totalAmount, additionalPayments, offerId }: ChangeDownPaymentArgs): Promise<Offer> =>
    this.request<Offer>({
      method: 'put',
      url: `/api/v2/loan-applications/down-payment/${offerId}`,
      service: BackendService.Purchase,
      data: {
        purchaseAdditionalDownPayments: additionalPayments,
        totalAmount,
      },
    });

  public updateLinkWasOpened = async (): Promise<void> => {
    await this.request({
      url: `api/v2/loan-applications/${this.shortToken}/link-was-opened`,
      service: BackendService.Purchase,
      method: 'put',
      trackingInfo: {
        eventName: EventsNames.updateApplicationStatus,
        props: {},
      },
    });
  };

  // Redirect URLs are built server-side by loan-application-service; the client sends no URL data.
  public getCustomerVerificationLink = async (): Promise<CustomerVerificationLinkResponse> => {
    return this.request<CustomerVerificationLinkResponse>({
      url: 'api/v2/loan-applications/extra-verification/verify-link',
      service: BackendService.LoanApplication,
      method: 'post',
      trackingInfo: {
        eventName: EventsNames.getCustomerVerificationLink,
      },
    });
  };

  public sendSmsVerificationLink = async (): Promise<void> => {
    return this.request({
      url: '/api/v2/loan-applications/extra-verification/send-extra-verify-sms',
      service: BackendService.LoanApplication,
      method: 'post',
    });
  };

  public getCustomer = async (): Promise<ApprovedUserResponse> => {
    const response = await this.request<ApprovedUserResponse>({
      url: '/api/v2/loan-applications/customer',
      service: BackendService.Purchase,
      method: 'get',
      trackingInfo: { eventName: EventsNames.getCustomerInfo },
    });
    return response;
  };

  public validateCustomer = async (dateOfBirth: string): Promise<ValidateUserResponse> => {
    const response = await this.request<ValidateUserResponse>({
      url: '/api/v2/loan-applications/validate-customer',
      service: BackendService.Purchase,
      method: 'post',
      trackingInfo: { eventName: EventsNames.validateUser },
      data: {
        dateOfBirth,
      },
    });
    return response;
  };

  public getDocumentByOfferId = async (offerId: number, documentName: string): Promise<DocumentPage> => {
    return this.request<DocumentPage>({
      method: 'get',
      url: `api/v2/loan-applications/${offerId}/generate/${documentName}`,
      service: BackendService.Purchase,
      trackingInfo: {
        eventName: EventsNames.getDocument,
        props: { offerId, documentName },
      },
    });
  };

  public getPrequalMerchantInfo = async ({
    pathContext,
  }: {
    pathContext: string;
  }): Promise<OnlineApplicationMerchant> => {
    return this.request<OnlineApplicationMerchant>({
      method: 'get',
      url: `/api/v1/epay/pre-qual/${pathContext}`,
      service: BackendService.Purchase,
      trackingInfo: {
        eventName: EventsNames.getPrequalMerchantInfo,
        props: { pathContext },
      },
    });
  };

  public createPrequalApplication = async ({
    pathContext,
    origin,
  }: {
    pathContext: string;
    origin: string;
  }): Promise<CreatePrequalApplicationResponse> => {
    return this.request<CreatePrequalApplicationResponse>({
      method: 'post',
      url: `/api/v2/loan-applications/pre-qual/${pathContext}`,
      service: BackendService.Purchase,
      data: {
        deviceId,
        origin,
      },
      trackingInfo: {
        eventName: EventsNames.createPrequalApplication,
        props: { pathContext, origin },
      },
    });
  };

  // Public EV-NOIA endpoints: no JWT, auth comes from the encrypted exvt token in the URL.
  public getExtraVerificationNoaaDocument = async (exvt: string): Promise<NotificationDocument> => {
    return this.request<NotificationDocument>({
      method: 'get',
      url: 'api/v2/loan-applications/extra-verification/noaa-document',
      service: BackendService.LoanApplication,
      params: { exvt },
      trackingInfo: {
        eventName: EventsNames.getDocument,
      },
    });
  };

  public markExtraVerificationDocumentDisplayed = async ({
    exvt,
    notificationType,
    fileName,
  }: {
    exvt: string;
    notificationType: string;
    fileName: string;
  }): Promise<void> => {
    return this.request({
      method: 'post',
      url: 'api/v2/loan-applications/extra-verification/documents/displayed',
      service: BackendService.LoanApplication,
      params: { exvt },
      data: { notificationType, fileName },
      trackingInfo: {
        eventName: EventsNames.markDocumentDisplayed,
        props: { notificationType },
      },
    });
  };

  public getNoaaDocument = async (): Promise<NoaaDocument> => {
    return this.request<NoaaDocument>({
      method: 'get',
      url: `api/v2/loan-applications/generate/noaa-document`,
      service: BackendService.Purchase,
      trackingInfo: {
        eventName: EventsNames.getDocument,
      },
    });
  };

  public getNotificationDocument = async (notificationType: NotificationType): Promise<DocumentPage> => {
    return this.request<DocumentPage>({
      method: 'get',
      url: `api/v2/loan-applications/generate/document`,
      params: { notificationType },
      service: BackendService.LoanApplication,
      trackingInfo: {
        eventName: EventsNames.getDocument,
        props: { notificationType },
      },
    });
  };

  public sendCSOAgreement = async (offerId: number): Promise<void> => {
    return this.request({
      method: 'post',
      url: `api/v2/loan-applications/${offerId}/cso-agreement`,
      service: BackendService.Purchase,
      trackingInfo: {
        eventName: EventsNames.sendCSOAgreement,
      },
    });
  };

  public sendCSODisclosure = async (offerId: number): Promise<void> => {
    return this.request({
      method: 'post',
      url: `api/v2/loan-applications/${offerId}/cso-disclosure`,
      service: BackendService.Purchase,
      trackingInfo: {
        eventName: EventsNames.sendCSODisclosure,
      },
    });
  };

  public markNoaaPresented = async ({
    offerId,
    noaaType,
    fileName,
  }: {
    offerId: number;
    noaaType: string;
    fileName: string;
  }): Promise<void> => {
    return this.request({
      method: 'put',
      url: `api/v2/loan-applications/${offerId}/noaa-presented`,
      service: BackendService.Purchase,
      params: { noaaType, fileName },
      trackingInfo: {
        eventName: EventsNames.markNoaaPresented,
        props: { offerId, noaaType },
      },
    });
  };

  public getNoaa5Document = async (): Promise<Noaa5Document> => {
    return this.request<Noaa5Document>({
      method: 'get',
      url: `api/v2/loan-applications/generate/noaa5-document`,
      service: BackendService.Purchase,
      trackingInfo: {
        eventName: EventsNames.getDocument,
      },
    });
  };

  public markNoaa5Presented = async ({
    offerId,
    fileName,
    noaaType,
  }: {
    offerId: number;
    fileName: string;
    noaaType: string;
  }): Promise<void> => {
    return this.request({
      method: 'put',
      url: `api/v2/loan-applications/${offerId}/noaa5-presented`,
      service: BackendService.Purchase,
      params: { fileName, noaaType },
      trackingInfo: {
        eventName: EventsNames.markNoaa5Presented,
        props: { offerId, noaaType },
      },
    });
  };

  public markDocumentDisplayed = async ({
    offerId,
    notificationType,
    fileName,
  }: {
    offerId: number;
    notificationType: string;
    fileName: string;
  }): Promise<void> => {
    return this.request({
      method: 'post',
      url: `api/v2/loan-applications/${offerId}/documents/displayed`,
      service: BackendService.LoanApplication,
      data: { notificationType, fileName },
      trackingInfo: {
        eventName: EventsNames.markDocumentDisplayed,
        props: { offerId, notificationType },
      },
    });
  };
}
